import { ChangeEvent } from 'react';
import { WorkBook, WorkSheet, utils as XLSXUtils, read as XLSXRead } from 'xlsx';
import { CountryList, PriceList, PriceListMetadata, ZoneCharges, ZoneChargeType, Zone, rateApplyFor } from './Type';
import { MAX_ZONEWISE_WT } from '@Constants/Shipment';
import { globalStateType } from '@Reducers/Global/Type';

export async function transformExcelToRateObj(
  fileEvent: ChangeEvent<HTMLInputElement>,
  rateApplyFor: rateApplyFor | undefined,
  alternateCountryNameObj: globalStateType['alternateCountryNameObj'],
  maxWeight?: number | null,
) {
  const workbook = await getXLXWorkbook(fileEvent);

  const { zoneData: zone, zoneChargeObj } = await zoneUpload(workbook, alternateCountryNameObj);

  const { rateData: rate, rateHeaderSequence } = await rateUpload(workbook, rateApplyFor, maxWeight);

  console.log({ rate, zone });

  if (zone && rate) {
    const combinedData = combindRateWithZone(rate, zone, zoneChargeObj);
    console.log({ combinedData });
    if (checkedAllZones(combinedData, rateHeaderSequence, rateApplyFor)) {
      return { zone: combinedData };
    }
  }
}

function checkedAllZones(combinedData: Zone[], rateHeaderSequence: string[], rateApplyFor: rateApplyFor | undefined) {
  const zoneNotFound: string[] = [];
  let emptyZoneWiseList: string[] = [];
  rateHeaderSequence &&
    rateHeaderSequence.forEach((item, index) => {
      const zoneData = combinedData.find(x => x.zoneName === item.trim());
      // console.log({ zoneData });
      if (!zoneData) return zoneNotFound.push(item.trim());
      // if (!zoneData.priceList.length) emptyZoneWiseList.push(zoneData.zoneName);
      zoneData.ratePosition = index;
    });
  if (rateApplyFor === 'zone-wise') {
    emptyZoneWiseList = combinedData.filter(x => !rateHeaderSequence.includes(x.zoneName)).map(x => x.zoneName);
    if (emptyZoneWiseList.length) throw { errorMsg: `No markup found for Zones ${emptyZoneWiseList.join(', ')}` };
  }
  if (zoneNotFound.length) throw { errorMsg: `No zone found for Zone Name ${zoneNotFound.join(', ')}` };
  return true;
}

export async function getXLXWorkbook(event: ChangeEvent<HTMLInputElement>) {
  const file = event.target.files?.[0];
  if (!file) throw { errorMsg: 'File now found' };
  const fileName = file.name;
  if (!isXlsFile(fileName)) throw { errorMsg: 'Not a xls file' };
  const workbook = await readWorkBook(file);
  return workbook;
}

export function isXlsFile(fileName: string) {
  return validateFile(fileName, ['xls', 'xlsx']);
}

export function convertExcelToPlainJson(workbook: WorkSheet) {
  return XLSXUtils.sheet_to_json(workbook);
}

export function validateFile(fileName: string, expectedExtension: string[] = []) {
  if (expectedExtension && !Array.isArray(expectedExtension)) {
    expectedExtension = [expectedExtension];
  }
  const fileExtention = fileName.split('.').pop() as string;
  if (fileExtention && expectedExtension.includes(fileExtention.toLowerCase())) return true;
  return false;
}

export function readWorkBook(file: File) {
  return new Promise<WorkBook>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = _event => {
      // let data = event.target.result;
      const data = reader.result;
      const workbook = XLSXRead(data, { type: 'binary', cellNF: true });
      resolve(workbook);
    };
    reader.onerror = error => {
      reject(error);
    };
    return reader.readAsBinaryString(file);
  });
}

/**
 * Checks whether an Excel workbook follows the Speedbox Standard / Sample Rate Sheet format
 * based on the official reference:
 * https://s3.ap-south-1.amazonaws.com/speedbox-static-file-2.0/Sample%2BBase%2BRate%2BSheet+(1).xlsx
 *
 * Requirements:
 * 1. Workbook must contain all 3 sheets: 'Zones', 'Rates', and 'Other Charges' (case-insensitive).
 * 2. 'Zones' sheet has 7 columns in exact order:
 *    [Pincode, City, State, Country, Country Code, Zone Name, Transit Days]
 * 3. 'Rates' sheet has 'Kgs' / 'Weight' / blank in col 0, followed by zone columns.
 * 4. 'Other Charges' sheet has 7 columns:
 *    [CHARGE NAME, VALUE, VALUE TYPE..., MIN, MAX, GLOBAL CHARGES APPLIED, ZONE NAMES]
 */
export function isExistingRateSheetFormat(workbook: WorkBook): boolean {
  if (!workbook || !workbook.SheetNames || !Array.isArray(workbook.SheetNames)) {
    return false;
  }

  const sheetNames = workbook.SheetNames;

  // 1. Must contain all 3 required sheets (case-insensitive)
  const zoneSheetName = sheetNames.find(name => /^zones$/i.test(name.trim()) || /zones/i.test(name));
  const rateSheetName = sheetNames.find(name => /^rates$/i.test(name.trim()) || /rates/i.test(name));
  const chargeSheetName = sheetNames.find(name => /^other\s*charges$/i.test(name.trim()) || /charge/i.test(name));

  if (!zoneSheetName || !rateSheetName || !chargeSheetName) {
    return false;
  }

  // 2. Check 'Zones' sheet header structure
  const zoneSheet = workbook.Sheets[zoneSheetName];
  if (!zoneSheet) return false;

  const zoneRows = XLSXUtils.sheet_to_json<unknown[]>(zoneSheet, { header: 1, blankrows: false, defval: '' });
  if (!zoneRows || zoneRows.length < 1) return false;

  const zoneHeaderRow = (zoneRows[0] || []).map(cell =>
    String(cell ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/\s+/g, ' '),
  );

  // Expected 7 columns in Zones sheet:
  // Pincode, City, State, Country, Country Code, Zone Name, Transit Days
  const expectedZoneHeaders = ['pincode', 'city', 'state', 'country', 'country code', 'zone name', 'transit days'];

  const matchesZones =
    zoneHeaderRow.length >= 7 &&
    expectedZoneHeaders.every((expected, idx) => {
      const actual = zoneHeaderRow[idx] || '';
      return (
        actual === expected ||
        (expected === 'country code' && actual.includes('code')) ||
        (expected === 'zone name' && actual.includes('zone')) ||
        (expected === 'transit days' && actual.includes('transit'))
      );
    });

  if (!matchesZones) {
    return false;
  }

  // 3. Check 'Rates' sheet header structure
  const rateSheet = workbook.Sheets[rateSheetName];
  if (!rateSheet) return false;

  const rateRows = XLSXUtils.sheet_to_json<unknown[]>(rateSheet, { header: 1, blankrows: false, defval: '' });
  if (!rateRows || rateRows.length < 1) return false;

  const rateHeaderRow = (rateRows[0] || []).map(cell =>
    String(cell ?? '')
      .trim()
      .toLowerCase(),
  );

  // First column is Kgs, kg, weight, or blank A1 cell
  const firstRateCol = rateHeaderRow[0] || '';
  const isRateWeightCol =
    firstRateCol === 'kgs' || firstRateCol === 'kg' || firstRateCol.includes('weight') || !rateSheet.A1;

  // Must have multiple columns (weight column + zone columns)
  const matchesRates = isRateWeightCol && rateHeaderRow.length >= 2;

  if (!matchesRates) {
    return false;
  }

  // 4. Check 'Other Charges' sheet header structure
  const chargeSheet = workbook.Sheets[chargeSheetName];
  if (!chargeSheet) return false;

  const chargeRows = XLSXUtils.sheet_to_json<unknown[]>(chargeSheet, { header: 1, blankrows: false, defval: '' });
  if (!chargeRows || chargeRows.length < 1) return false;

  const chargeHeaderRow = (chargeRows[0] || []).map(cell =>
    String(cell ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/\s+/g, ' '),
  );

  // Expected 7 columns in Other Charges sheet:
  // CHARGE NAME | VALUE | VALUE TYPE... | MIN | MAX | GLOBAL CHARGES APPLIED | ZONE NAMES
  const matchesCharges =
    chargeHeaderRow.length >= 7 &&
    chargeHeaderRow[0].includes('charge') &&
    chargeHeaderRow[1].includes('value') &&
    chargeHeaderRow[2].includes('type') &&
    chargeHeaderRow[3].includes('min') &&
    chargeHeaderRow[4].includes('max') &&
    chargeHeaderRow[5].includes('charges applied') &&
    chargeHeaderRow[6].includes('zone');

  if (!matchesCharges) {
    return false;
  }

  return true;
}

export async function checkIfFileIsStandardRateSheet(file: File): Promise<boolean> {
  try {
    if (!isXlsFile(file.name)) {
      return false;
    }
    const workbook = await readWorkBook(file);
    return isExistingRateSheetFormat(workbook);
  } catch {
    return false;
  }
}

async function zoneUpload(workbook: WorkBook, alternateCountryNameObj: globalStateType['alternateCountryNameObj']) {
  // const workbook = await getXLXWorkbook(fileEvent);
  const { zoneDataArray, zoneChargeObj } = await processExcelDataToJson(workbook, alternateCountryNameObj);
  const zoneData = await excelDataToZoneFormat(zoneDataArray);
  const csvInJson = await convertExcelToPlainJson(workbook.Sheets[workbook.SheetNames[0]]);
  return { zoneData, zoneChargeObj, zoneSheetJson: csvInJson };
}

const chargeSheetRegEx = /charge/i;
const zoneSheetRegEx = /zones/i;
const rateSheetRegEx = /rates/i;
// Erro Obj
// {
//  errorMsg: ""
// }

const chargesType = {
  'per kg': 1,
  percent: 1,
  fixed: 1,
};

interface FormattedZoneDataArray {
  courierServiceName: string;
  cityArray: string[];
  stateArray: string[];
  countryArray: string[];
  countryName: string;
  zoneName: string;
  destinationCode: string;
  transitTime: string;
  reputation: string;
  pincodeArray: string[];
}

interface ZoneToChargesMap {
  [zoneName: string]: { [otherCharge: string]: ZoneCharges };
}

export function processExcelDataToJson(
  workbook: WorkBook,
  alternateCountryNameObj: globalStateType['alternateCountryNameObj'],
) {
  // let processedZoneData = new Array();
  const header = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  //workbook.SheetNames.forEach((sheetName, index) => {
  const sheetName = workbook.SheetNames[0];
  const chargeWorkSheetName = workbook.SheetNames.find(x => x.match(chargeSheetRegEx)) as string;
  const zoneWorkSheetName = workbook.SheetNames.find(x => x.match(zoneSheetRegEx)) as string;
  const workSheet = workbook.Sheets[zoneWorkSheetName];
  const chargeWorkSheetObj = workbook.Sheets[chargeWorkSheetName];

  if (!workSheet) throw { errorMsg: `Zone Sheet not found. Please make sure the zone sheet name is "zones"` };

  const chargeWorkSheetArr = XLSXUtils.sheet_to_json(chargeWorkSheetObj, { header, blankrows: false, defval: '' });

  chargeWorkSheetArr.shift();
  const zoneCharges: ZoneToChargesMap = {};
  chargeWorkSheetArr.forEach(
    (charge: { A: string; B: string; C: string; D: string; E: string; F: string; G: string }, index) => {
      const chargeName = charge.A.toString();
      if (!chargeName) throw { errorMsg: `Charge Name is required (Cell:A${index + 2})` };

      const chargeValueString = charge.B.toString();
      let chargeValue: number;
      if (!chargeValueString)
        throw { errorMsg: `Charge value is required (Charge:${chargeName}, Error Cell:B${index + 2})` };
      if (!Number(chargeValueString))
        throw { errorMsg: `Charge value can only be a number (Charge:${chargeName}, Error Cell:B${index + 2})` };
      else chargeValue = Number(chargeValueString);

      const chargeType = charge.C.toString().toLowerCase() as ZoneChargeType;
      if (!chargeType) throw { errorMsg: `Charge type is required (Charge:${chargeName}, Error Cell::C${index + 2})` };
      if (!(chargeType in chargesType))
        throw {
          errorMsg: `Charge type can only be "per kg", "percent" or "fixed" (Charge:${chargeName}, Error Cell:C${
            index + 2
          })`,
        };

      let min: number | string | null = charge.D.toString();
      if (min) {
        if (Number(min)) min = Number(min);
        else throw { errorMsg: `Min can only be a number (Charge: ${chargeName}, Error Cell:D${index + 2})` };
      } else min = null;

      let max: number | string | null = charge.E.toString();
      if (max) {
        if (Number(max)) max = Number(max);
        else throw { errorMsg: `Max can only be a number (Charge: ${chargeName}, Error Cell:E${index + 2})` };
      } else max = null;

      const chargesApplied = charge.F.toString()
        .split(',')
        .map(x => x.trim())
        .filter(x => x);

      const charge_zones_str: string = charge.G.toString().trim();
      if (!charge_zones_str) throw { errorMsg: `Zone is required (Charge: ${chargeName}, Error Cell:G${index + 2})` };
      const charge_zones: string[] = charge_zones_str.split(',').map(x => x.trim());

      const chargeObj: ZoneCharges = {
        name: chargeName,
        value: chargeValue,
        charge_type: chargeType,
        min,
        max,
        charges_applied: chargesApplied,
      };

      charge_zones.forEach(x => {
        const currZoneCharge = (zoneCharges[x] = zoneCharges[x] || {});
        const formattedChargeName = chargeName.toLowerCase();
        if (currZoneCharge[formattedChargeName])
          throw { errorMsg: `Cannot have duplicate charges (Charge: ${chargeName}, Error Cell:G${index + 2})` };
        currZoneCharge[formattedChargeName] = chargeObj;
      });
    },
  );

  const zoneDataArray = XLSXUtils.sheet_to_json(workSheet, { header, blankrows: false, defval: '' });
  const formattedZoneDataArray = XLSXUtils.sheet_to_json(workSheet, {
    header,
    blankrows: false,
    defval: '',
    raw: false,
  });

  /*** remove Header */
  zoneDataArray.shift();
  formattedZoneDataArray.shift();

  /*********** */
  const FormattedZoneDataArray: FormattedZoneDataArray[] = zoneDataArray.map(
    (
      zone: {
        A: string | number;
        B: string;
        C: string;
        D: string;
        E: string;
        F: string;
        G: string;
        H: string;
      },
      index,
    ) => {
      const formattedZone = formattedZoneDataArray[index] as { A?: string | number } | undefined;
      const rawPincodeVal =
        formattedZone?.A !== undefined && formattedZone?.A !== null && String(formattedZone?.A).trim() !== ''
          ? formattedZone.A
          : zone.A;
      const pincodeArray = rawPincodeVal?.toString().trim().toLowerCase() || '';
      const cityArray = zone.B.toString().trim().toLowerCase();
      const stateArray = zone.C.toString().trim().toLowerCase();
      const countryArray = zone.D.toString().trim().toLowerCase();
      const destinationCode = zone.E.toString().trim();
      const zoneName = zone.F.toString().trim() || 'N/A';
      const transitTime = zone.G || '';
      const reputation = zone.H || '';

      let zoneCountryName = '';
      for (const country of countryArray.split(',')) {
        const lowerCountry = country.trim();
        zoneCountryName = alternateCountryNameObj[lowerCountry]?.name?.toLowerCase();
        if (zoneCountryName) break;
      }
      if (!zoneCountryName)
        throw { errorMsg: 'No country found with name ' + countryArray + ` (Sheet: Zone, Cell: C${index + 2})` };

      const dataObj = {
        courierServiceName: sheetName,
        cityArray: new Array<string>(),
        stateArray: new Array<string>(),
        countryArray: new Array<string>(),
        countryName: countryArray,
        zoneName,
        destinationCode,
        transitTime,
        reputation,
        pincodeArray: new Array<string>(),
      };
      dataObj.cityArray = cityArray
        ? cityArray
            .split(',')
            .slice()
            .map(x => x.trim())
        : [''];
      dataObj.stateArray = stateArray
        ? stateArray
            .split(',')
            .slice()
            .map(x => x.trim())
        : [''];
      dataObj.countryArray = [zoneCountryName];
      dataObj.pincodeArray = pincodeArray // Add this block
        ? pincodeArray
            .split(',')
            .slice()
            .map(x => x.trim())
        : [''];
      return { ...dataObj };
    },
  );

  return { zoneDataArray: [FormattedZoneDataArray], zoneChargeObj: zoneCharges };
}

interface ZoneArrayListObj {
  [zoneName: string]: {
    [Zones: string]: Array<CountryList>;
  };
}

export function excelDataToZoneFormat(processedData: FormattedZoneDataArray[][]) {
  return new Promise<ZoneArrayListObj>(resolve => {
    const allZoneArrayList: ZoneArrayListObj = {};
    processedData.forEach(zoneDataArray => {
      zoneDataArray.forEach(element => {
        // const cityOne = element.cityArray[0] ? element.cityArray[0].toString().trim() + ',' : '';
        // const stateOne = element.stateArray[0] ? element.stateArray[0].toString().trim() + ',' : '';
        // const countryOne = element.countryArray[0] ? element.countryArray[0].toString().trim() : '';
        // const countryName = cityOne + stateOne + countryOne;
        const countryListObject = {
          countryName: element.countryName,
          countryCode: element.destinationCode,
          preferredName: new Array<string>(),
          transitionTime: Number(element.transitTime) ? Number(element.transitTime) : 0,
          reputation: Number(element.reputation) ? Number(element.reputation) : 0,
          city: element.cityArray,
          state: element.stateArray,
          pincode: element.pincodeArray,
        };

        if (!allZoneArrayList[element.zoneName]) {
          allZoneArrayList[element.zoneName] = {};
        }

        if (!allZoneArrayList[element.zoneName][element.courierServiceName])
          allZoneArrayList[element.zoneName][element.courierServiceName] = new Array<CountryList>();

        element.cityArray.forEach((city: string) => {
          element.stateArray.forEach((state: string) => {
            element.countryArray.forEach((country: string) => {
              element.pincodeArray.forEach((pincode: string) => {
                const cityName = city ? city.toString().trim() + ',' : '';
                const stateName = state ? state.toString().trim() + ',' : '';
                const countryName = country ? country.toString().trim() : '';
                const pincodeName = pincode ? pincode.toString().trim() + ',' : '';
                const preferredName = pincodeName + cityName + stateName + countryName;
                countryListObject.preferredName.push(preferredName);
              });
            });
          });
        });
        allZoneArrayList[element.zoneName][element.courierServiceName].push(countryListObject);
      });
    });
    resolve(allZoneArrayList);
  });
}

function getNumByRegex(stringData: string, regEx: RegExp) {
  let { value: matches } = stringData.matchAll(regEx).next();
  if (!matches) return null;
  matches = matches.filter((x: string) => Number(x));
  if (matches.length === 0) return null;
  const numMatched = matches[0];
  return Number(numMatched);
}

const addOnRegex = /every\s*(\d+(\.\d+)?)/gi;
const multiplierRegex = /per\s*(\d+(\.\d+)?)/gi;
const flatRegex = /flat/gi;
const weightRegex = /-*(\d*\.?\d*)/g;

interface SpecialParams {
  weight: number;
  value: number;
  disWt: string;
  isAddOn?: boolean;
  isMultiplier?: boolean;
  unit?: number;
  flat?: boolean;
  lastRowObj?: PriceList;
}
interface FormattedRateData {
  [key: string]: PriceList[];
}
export function processRateSheetData(workbook: WorkBook) {
  return new Promise<FormattedRateData>(resolve => {
    const rateWorkSheetName = workbook.SheetNames.find(x => x.match(rateSheetRegEx)) as string;
    const workSheet = workbook.Sheets[rateWorkSheetName];

    if (!workSheet) throw { msg: `Rate Sheet not found. please make sure the rate sheet name is "rates"` };

    const ratesSheetCsvStr = XLSXUtils.sheet_to_csv(workSheet, { blankrows: false });
    const ratesSheetCsv: string[] = ratesSheetCsvStr.split('\n');
    let headers = ratesSheetCsv.shift()?.split(',');
    if (!headers) headers = [];
    const rateHeadersPos: { name: string; value?: string; rows?: SpecialParams[] }[] = [];
    let emptyHeaderPos: boolean | null = null;

    if (!workSheet.A1) {
      headers[0] = 'Weight';
    }

    headers.forEach((header, index) => {
      if (header && emptyHeaderPos !== null) {
        // previous row position is equals to current row index
        throw { errorMsg: `Zone Name in Header cannot be empty Error Cell:A${index}` };
      } else if (!header && emptyHeaderPos === null) {
        emptyHeaderPos = true;
        return;
      }
      if (index === 0) return rateHeadersPos.push({ name: 'weight', value: header });
      return rateHeadersPos.push({
        name: header,
        rows: [],
      });
    });

    let lastWtObj: { weight: number; wtCellVal: string } | null = null;
    ratesSheetCsv.forEach(function (rowString, index) {
      const rowArr = rowString.split(',');

      const wtCellVal = rowArr.shift();
      if (!wtCellVal) return;

      const addOnUnit = getNumByRegex(wtCellVal, addOnRegex);
      const mulUnit = getNumByRegex(wtCellVal, multiplierRegex);
      const flat = wtCellVal.match(flatRegex);

      /****IF MULTIPLIER OR ADDON OR FLAT ROW THEN ADD METADATA TO ALL ZONE ROWS */
      if (addOnUnit || mulUnit || flat) {
        const specialParams: SpecialParams = {
          weight: 0,
          value: 0,
          disWt: '0',
        };
        if (addOnUnit) {
          specialParams.isAddOn = true;
          specialParams.unit = addOnUnit;
        } else if (mulUnit) {
          specialParams.isMultiplier = true;
          specialParams.unit = mulUnit;
        } else if (flat) {
          specialParams.flat = true;
        }
        console.log(rateHeadersPos, 'hjkl');
        rateHeadersPos.forEach((_, index) => {
          if (index === 0) return;
          if (!_.rows) _.rows = [];
          _.rows.push(specialParams);
        });
        return;
      }

      const weight = getNumByRegex(wtCellVal, weightRegex);
      /**INDEX + 2 because this array doesn't include header row */
      if (!weight) throw { errorMsg: `Error weight value found at Cell A${index + 2}` };
      if (weight < 0)
        throw { errorMsg: `Weight cannot be negative (Error Cell: A${index + 2}, Error Value: ${wtCellVal})` };
      /***************************************************************** */

      rowArr.forEach(function (cellVal, index) {
        if (!cellVal) return;
        const rateHeaderIndex = index + 1;
        if (!rateHeadersPos[rateHeaderIndex]) return;

        const rows = rateHeadersPos[rateHeaderIndex].rows;
        const value = Number(cellVal);
        if (!value) {
          throw {
            errorMsg: `Error rate value found for Weight: ${weight}, Zone: ${rateHeadersPos[rateHeaderIndex].name}.
            Please enter only number as rates`,
          };
        }

        const { weight: lastWt = -1, wtCellVal: lastDisWt } = lastWtObj || {};

        if (weight < lastWt)
          throw {
            errorMsg: `Please add rates from minimum to maximum sequence, Rate for Weight ${wtCellVal} cannot appear after Weight ${lastDisWt}`,
          };

        rows?.push({
          weight,
          value,
          disWt: wtCellVal,
        });
      });
      /**STORE LAST WT DETAILS TO COMPARE IF NEXT WT IS LOWER THAN CURRENT WT IF YES THEN THROW ERROR */
      lastWtObj = { weight, wtCellVal };
    });

    const v2Data: FormattedRateData = {};
    for (const { name, rows } of rateHeadersPos) {
      if (name === 'weight') continue;
      const priceList: PriceList[] = [];
      v2Data[name] = priceList;
      let specialParams: SpecialParams[] = [];
      for (let indx = 0; indx < (rows?.length || 0); indx++) {
        const row = rows?.[indx] as SpecialParams;
        if (row.isAddOn || row.isMultiplier) {
          specialParams.push(row);
          if (row.isAddOn) {
            const lastRowObj = priceList[priceList.length - 1];
            row.lastRowObj = lastRowObj;
          }
          continue;
        } else if (row.flat) {
          specialParams = [row];
          continue;
        }
        const { weight, value, disWt } = row;
        if (specialParams.length >= 2) {
          const specialRateType = row.isAddOn ? 'Every Kg' : 'Per Kg';
          throw {
            errorMsg: `You cannot have ${specialRateType} rate without flat rate row, Error Zone: ${name}, Error Weight:${weight}`,
          };
        }

        const { isAddOn, isMultiplier, unit, flat, lastRowObj } = specialParams[0] || {};
        let metadata: PriceListMetadata | undefined = undefined;
        const priceObj: PriceList = {
          weight,
          price: value,
        };

        if (flat) {
          const lastPriceObj = priceList[priceList.length - 1];
          if (lastPriceObj?.metadata?.addOn || lastPriceObj?.metadata?.multiplier) {
            lastPriceObj.weight = weight - 0.001;
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          metadata = {} as any;
          if (!metadata) throw { errorMsg: 'Metadata error' };
          metadata.disWt = disWt;
          metadata.flat = true;
          specialParams = [];
        }

        if (isAddOn || isMultiplier) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          metadata = {} as any;
          if (!metadata) throw { errorMsg: 'Metadata error' };
          metadata.disWt = disWt;
          metadata.perUnit = unit as number;
          const lastPriceObj = priceList[priceList.length - 1];
          /**IF LAST RATE IS SPECIAL RATE THEN CURRENT WT SHOULD BE LAST BLOCK WT value
           * 12+ 10
           * 14+ 20
           * FOR 12+ weight row, weight value would be 14 since 10 is to be charged until 14kg
           */
          lastPriceObj.weight = weight;
          // if (lastPriceObj?.metadata?.addOn || lastPriceObj?.metadata?.multiplier) {
          //   lastPriceObj.weight = weight;
          // }
          /**IF LAST ROW IS SPECIAL ROW THEN ASSIGN MAX_SAFE_INTEGER TO THIS ROW WEIGHT AS THERE IS NO NEXT ROW. QUERY WILL FILTER zone.maxWeight if present */
          if (indx === (rows?.length || 0) - 1) priceObj.weight = Number.MAX_SAFE_INTEGER;
        }

        if (isAddOn) {
          if (!metadata) throw { errorMsg: 'Metadata error' };
          metadata.addOn = true;
          metadata.lastWt = lastRowObj?.weight as number;
          metadata.lastWtValue = lastRowObj?.price as number;
        } else if (isMultiplier) {
          if (!metadata) throw { errorMsg: 'Metadata error' };
          metadata.multiplier = true;
        }

        if (metadata) priceObj.metadata = metadata;

        priceList.push(priceObj);
      }
    }

    resolve(v2Data);
  });
}

async function processZoneWiseRateSheetData(workbook: WorkBook, maxWeight?: number | null) {
  const rateHeaderSequence: string[] = [];
  const rateWorkSheetName = workbook.SheetNames.find(x => x.match(rateSheetRegEx)) as string;
  const workSheet = workbook.Sheets[rateWorkSheetName];

  if (!workSheet) throw { msg: `Rate Sheet not found. please make sure the rate sheet name is "rates"` };

  const ratesSheetCsvStr = XLSXUtils.sheet_to_csv(workSheet, { blankrows: false, FS: ';' });
  const ratesSheetCsv: string[] = ratesSheetCsvStr.split('\n');
  const maxWeightRow = maxWeight || MAX_ZONEWISE_WT;
  /**REMOVE HEADERS */
  ratesSheetCsv.shift()?.split(',');
  const resJSON: { [key: string]: PriceList[] } = {};

  ratesSheetCsv.map((rowCsv, index) => {
    const [zone, markup] = rowCsv.split(';');
    zone.split(',').map((z: string) => {
      z = z.trim();
      if (resJSON[z]) throw { errorMsg: `Zone ${z} found with multiple markup, ErrorCell:A${index + 2}` };
      if (isNaN(Number(markup))) throw { errorMsg: `Markup should be a number ErrorCell:B${index + 2}` };
      rateHeaderSequence.push(z);
      resJSON[z] = [
        { weight: 0.001, percent: Number(markup) },
        { weight: maxWeightRow, percent: Number(markup) },
      ];
    });
    if (!zone || !zone.trim())
      throw { errorMsg: `Atleast one zone is required to apply markup on, Error Cell:A${index + 2}` };
  });
  return { rateData: resJSON, rateHeaderSequence };
}

export async function rateUpload(
  workbook: WorkBook,
  rateApplyFor: rateApplyFor | undefined,
  maxWeight?: number | null,
) {
  // const workbook = await getXLXWorkbook(fileEvent);

  const rateWorkSheetName = workbook.SheetNames.find(x => x.match(/rates/i)) as string;
  const workSheet = workbook.Sheets[rateWorkSheetName];
  if (!workSheet) throw { errorMsg: `Rate Sheet not found. please make sure the rate sheet name is "rates"` };

  const csvInJson = await convertExcelToPlainJson(workSheet);
  let rateHeaderSequence: string[] = Object.keys(csvInJson[0] as { [k: string]: object });
  if (workSheet.A1) {
    const weightColumnKey = workSheet.A1.v;
    const weightIndex = rateHeaderSequence.findIndex(head => head?.trim?.() === weightColumnKey);

    if (weightIndex > -1) {
      rateHeaderSequence.splice(weightIndex, 1);
    }
  }

  rateHeaderSequence = rateHeaderSequence.filter(x => x !== '__EMPTY');

  let rateData = {};
  if (rateApplyFor === 'rate-sheet' || rateApplyFor === 'custom-rate') rateData = await processRateSheetData(workbook);
  else if (rateApplyFor === 'zone-wise') {
    const { rateData: zoneWiseRateData, rateHeaderSequence: zoneWiseRateHeaderSequence } =
      await processZoneWiseRateSheetData(workbook, maxWeight);
    rateData = zoneWiseRateData;
    rateHeaderSequence = zoneWiseRateHeaderSequence;
  }
  return { rateData, rateHeaderSequence, rateSheetJson: csvInJson };
}

export function combindRateWithZone(
  rateData: FormattedRateData,
  zoneData: ZoneArrayListObj,
  zoneCharges: ZoneToChargesMap,
): Zone[] {
  // const _rateKey = Object.keys(rateData)[0];
  return Object.keys(zoneData).map((zoneName, _index) => {
    const zoneKey = Object.keys(zoneData[zoneName])[0];
    let zoneChargeArr: ZoneCharges[] = [];
    if (zoneCharges[zoneName]) {
      const ZoneChargesMap = zoneCharges[zoneName];
      zoneChargeArr = Object.values(ZoneChargesMap);
    }
    return {
      zoneName: zoneName,
      charges: zoneChargeArr,
      priceList: rateData[zoneName],
      countryList: zoneData[zoneName][zoneKey],
    };
  });
}

export interface ConvertApiResponsePayload {
  vendor?: string;
  rateType?: string;
  sourceS3Key?: string;
  zones?: Array<{
    vendorCountry?: string;
    country?: string;
    countryCode?: string;
    zone: string | number;
    transitDays?: number | string | null;
  }>;
  rates?: {
    slabs?: Array<{
      weightKg: number;
      rates: Record<string, number>;
    }>;
    brackets?: Array<{
      weightFrom: number;
      weightTo: number;
      perKg: Record<string, number>;
    }>;
  };
  otherCharges?: Array<{
    name?: string;
    charge?: number | string;
    unit?: string;
    [key: string]: unknown;
  }>;
  zone?: Zone[];
  data?: ConvertApiResponsePayload;
  [key: string]: unknown;
}

export function transformConvertResponseToZones(
  response: ConvertApiResponsePayload,
  alternateCountryNameObj?: globalStateType['alternateCountryNameObj'],
): Zone[] {
  if (!response) return [];

  const normalizedData: ConvertApiResponsePayload =
    response.data && typeof response.data === 'object' ? response.data : response;

  // If already in Zone[] format
  if (Array.isArray(normalizedData.zone) && normalizedData.zone.length > 0) {
    return normalizedData.zone;
  }

  const { zones: rawZones = [], rates, otherCharges = [] } = normalizedData;
  const slabs = rates?.slabs || [];
  const brackets = rates?.brackets || [];

  // 1. Collect all unique zone names in order
  const zoneOrderSet = new Set<string>();

  slabs.forEach(slab => {
    if (slab.rates) {
      Object.keys(slab.rates).forEach(z => zoneOrderSet.add(z.trim()));
    }
  });

  brackets.forEach(bracket => {
    if (bracket.perKg) {
      Object.keys(bracket.perKg).forEach(z => zoneOrderSet.add(z.trim()));
    }
  });

  rawZones.forEach(z => {
    if (z.zone !== undefined && z.zone !== null) {
      zoneOrderSet.add(String(z.zone).trim());
    }
  });

  const allZoneNames = Array.from(zoneOrderSet);

  // 2. Group rawZones by zoneName
  const countriesByZoneMap = new Map<string, CountryList[]>();

  rawZones.forEach(item => {
    const zoneName = String(item.zone ?? '').trim();
    if (!zoneName) return;

    if (!countriesByZoneMap.has(zoneName)) {
      countriesByZoneMap.set(zoneName, []);
    }

    let resolvedCountryName = item.country || item.vendorCountry || '';
    if (alternateCountryNameObj && item.country) {
      const lower = item.country.toLowerCase().trim();
      if (alternateCountryNameObj[lower]?.name) {
        resolvedCountryName = alternateCountryNameObj[lower].name;
      }
    }

    const countryObj: CountryList = {
      countryName: resolvedCountryName,
      countryCode: item.countryCode || '',
      preferredName: [resolvedCountryName],
      transitionTime: Number(item.transitDays) || 0,
      reputation: 0,
      city: [''],
      state: [''],
      pincode: [''],
    };

    countriesByZoneMap.get(zoneName)?.push(countryObj);
  });

  // 3. Build Zone[] array
  const resultZones: Zone[] = allZoneNames.map((zoneName, index) => {
    const priceList: PriceList[] = [];

    // Add weight slabs
    slabs.forEach(slab => {
      const price = Number(slab.rates?.[zoneName]);
      if (!isNaN(price)) {
        priceList.push({
          weight: Number(slab.weightKg),
          price: price,
        });
      }
    });

    // Add per-kg brackets
    brackets.forEach((bracket, bIdx) => {
      const perKgPrice = Number(bracket.perKg?.[zoneName]);
      if (!isNaN(perKgPrice)) {
        const isLast = bIdx === brackets.length - 1;
        const ceilingWeight = isLast || bracket.weightTo > 50000 ? Number.MAX_SAFE_INTEGER : Number(bracket.weightTo);
        const disWt = bracket.weightTo > 50000 ? `${bracket.weightFrom}+` : `${bracket.weightFrom}-${bracket.weightTo}`;

        const metadata: PriceListMetadata = {
          disWt,
          multiplier: true,
          addOn: false,
          perUnit: 1,
          lastWt: 0,
          lastWtValue: 0,
          flat: false,
        };

        priceList.push({
          weight: ceilingWeight,
          price: perKgPrice,
          metadata,
        });
      }
    });

    // Zone Charges
    const charges: ZoneCharges[] = [];
    if (Array.isArray(otherCharges)) {
      otherCharges.forEach(ch => {
        if (ch && typeof ch === 'object') {
          charges.push({
            name: String(ch.name || 'Additional Charge'),
            value: Number(ch.charge) || 0,
            charge_type: 'fixed',
            min: null,
            max: null,
            charges_applied: [],
          });
        }
      });
    }

    const countryList = countriesByZoneMap.get(zoneName) || [];

    return {
      zoneName: zoneName,
      charges: charges,
      priceList: priceList,
      countryList: countryList,
      ratePosition: index,
    };
  });

  return resultZones;
}
